import { FileRecord, ProjectRecord, UserType } from "@/types";
import axios from "@/libs/axios.ts";
import { DateRange } from "rsuite/DateRangePicker";
import dayjs from "dayjs";
import {
  SendPerformanceReportEmailBody,
  UpdatePerformanceReportBody,
  GeographicDistributionResponse,
} from "@/types/projects.ts";

export const updateProject = async (
  projectId: ProjectRecord["id"],
  body: FormData
) => {
  return axios.patch(`/project/update/${projectId}`, body);
};

export const archiveProjectReq = async (projectId: ProjectRecord["id"]) =>
  axios.delete(`/project/archive/${projectId}`);

export const deleteProjectReq = async (projectId: ProjectRecord["id"]) =>
  axios.delete(`/project/delete/${projectId}`);

export const restoreProjectReq = async (projectId: ProjectRecord["id"]) =>
  axios.post(`/project/restore/${projectId}`);

export const createProjectReq = async (body: FormData) => {
  return axios.post(`/project/create`, body);
};
export const restoreProjectFileReq = async (fileId: FileRecord["id"]) => {
  return axios.post(`/project/document/${fileId}`);
};

export const addDueDateProjectReq = async (
  id: ProjectRecord["id"],
  body: { extendToDate: string; note: string }
) => {
  return axios.post(`/project/extension/${id}`, body);
};
export const getAllProjectsReq = async (
  page = "1",
  pageLimit: any,
  search = "",
  filter = [] as string[],
  ptoLetterFilter = false,
  sort = "",
  sizingPassedFilter?: string,
  batteryOperationalized?: string
) => {
  const params = {
    page,
    pageLimit: pageLimit ?? "",
    search,
    filter: filter.join(","),
    pto: ptoLetterFilter ? ptoLetterFilter : "",
    sort,
    ...(sizingPassedFilter !== undefined && {
      sizing_passed: sizingPassedFilter,
    }),
    ...(batteryOperationalized !== undefined && {
      batteryOperationalized: batteryOperationalized,
    }),
  };
  return axios.get(`/project/list`, { params });
};

export const getProjectsByAccountNameReq = async (search = "") => {
  const params = {
    search,
  };
  return axios.get(`/project/list`, { params });
};

export const getProjectsFilesReq = async (
  projectId: ProjectRecord["id"],
  page = "1",
  pageLimit: any,
  search = "",
  filter = [] as string[],
  dateRangeValue: DateRange | null
) => {
  const params = {
    page,
    pageLimit: pageLimit ?? "",
    search,
    filter: filter.join(","),
    startDate: dateRangeValue?.length
      ? dayjs(dateRangeValue?.[0])?.format("YYYY-MM-DD")
      : "",
    endDate: dateRangeValue?.length
      ? dayjs(dateRangeValue?.[1])?.format("YYYY-MM-DD")
      : "",
  };
  return axios.get(`/project/document/${projectId}`, { params });
};

export const uploadProjectFileReq = async (
  projectId: ProjectRecord["id"],
  body: FormData
) => axios.post(`/project/document/add/${projectId}`, body);

export const uploadProjectDocumentReq = async (
  projectId: ProjectRecord["id"],
  body: FormData
) => axios.patch(`/document/add/${projectId}`, body);

export const getProjectReq = async (projectId: ProjectRecord["id"]) => {
  return axios.get(`/project/${projectId}`);
};

export const deleteProjectFileReq = async (fileId: FileRecord["id"]) => {
  return axios.delete(`/project/document/${fileId}`);
};

export const updateProjectFileReq = async (
  fileId: FileRecord["id"],
  body: Partial<FileRecord>
) => {
  return axios.patch(`/project/document/${fileId}`, body);
};

export const getProjectIdsReq = async (
  search = "",
  filter = [] as string[],
  sort = "",
  pto = "",
  sizingPassedFilter?: string,
  batteryOperationalized?: string
) => {
  const params = {
    ...(search && { search }),
    ...(sort && { sort }),
    ...(filter?.length && { filter: filter.join(",") }),
    ...(pto && { pto }),
    ...(sizingPassedFilter !== undefined && {
      sizing_passed: sizingPassedFilter,
    }),
    ...(batteryOperationalized !== undefined && {
      batteryOperationalized: batteryOperationalized,
    }),
    ids: true,
  };
  return axios.get(`/project/list`, { params });
};
export const getAllProjectsToExportReq = async (
  search = "",
  filter = [] as string[],
  pto: boolean | string = "",
  sizingPassedFilter?: string,
  batteryOperationalized?: string
) => {
  const params = {
    ...(search && { search }),
    ...(filter?.length && { filter: filter.join(",") }),
    ...(pto && { pto }),
    ...(sizingPassedFilter !== undefined && {
      sizing_passed: sizingPassedFilter,
    }),
    ...(batteryOperationalized !== undefined && {
      batteryOperationalized: batteryOperationalized,
    }),
    csv: true,
  };
  return axios.get(`/project/list`, { params });
};

export const getProjectsToExportReq = (projectIds: number[]) => {
  const params = {
    ids: projectIds?.join(","),
  };
  return axios.get(`/project/csv`, { params });
};

export const checkCustomerName = async (customerName: string) => {
  const params = {
    name: customerName,
  };
  return axios.get(`/project/customer-name/check`, { params });
};

export const sendPerformanceIncentiveEmailReq = async (
  projectIds: number[]
) => {
  const params = {
    projectIds: projectIds.join(","),
  };

  return axios.post(`/project/performance-incentive-email`, null, { params });
};

export const sendPerformanceReportEmailReq = async ({
  projectId,
  performanceReportId,
  quarterName,
  performanceYear,
}: SendPerformanceReportEmailBody) => {
  return axios.post(
    `/project/performance-report-email/${projectId}/${performanceReportId}`,
    { quarterName, performanceYear }
  );
};

export const updatePerformanceReportReq = async ({
  projectId,
  performanceReportId,
  quarterName,
  performanceYear,
}: UpdatePerformanceReportBody) => {
  return axios.patch(
    `/project/performance-report/${projectId}/${performanceReportId}`,
    { quarterName, performanceYear }
  );
};

export const validateAccountNumber = async (
  accountNumber: string,
  zipCode?: string,
  isSizingTool?: boolean
) => {
  const params = {
    accountNumber,
    ...(zipCode && { zipcode: zipCode }),
    ...(isSizingTool && { sizingTool: true }),
  };
  return axios.get(`/project/sdcp-project`, { params });
};

export const calculateSbsiReq = async (body: {
  accountNumber: string;
  estimated_kwh: number;
  total_cost: number;
  extra_incentives?: number;
}) => {
  return axios.post(`/project/calculate-sbsi`, body);
};

export const analyzeSizingToolReq = async (
  body: {
    accountNumber: string;
    existingSolarSystemSize: number;
    newSolarSystemSize: number;
    totalBatteryUsableCapacity: number;
    numberOfBatteries: number;
    zipCode: string;
    solar_installation_duration_less_than_12_months?: boolean;
    existing_battery_installed?: boolean;
    isDemo?: boolean;
  },
  signal?: AbortSignal
) => {
  return axios.post(`/project/size-tool`, body, { signal });
};

export const updateIncentivePaymentReq = async (
  projectId: ProjectRecord["id"],
  upfrontIncentivePayment: number
) => {
  return axios.put(`/project/${projectId}/incentive`, {
    newIncentiveAmount: upfrontIncentivePayment,
  });
};

export type RecalculateIncentiveResponse = {
  success: boolean;
  message: string;
  /** upfrontIncentivePayment is in cents, as stored. */
  project: { id: number; upfrontIncentivePayment: number };
  /** Amount before the recalculation, in cents. */
  previousIncentivePayment: number;
  isMarket: boolean;
};

/**
 * Re-runs the SBSI calculation against the account's current CRM eligibility and saves the
 * result. This is the only way a saved project's incentive gets recalculated (SDCP-1305) —
 * editing the project no longer touches it.
 */
export const recalculateIncentiveReq = async (
  projectId: ProjectRecord["id"]
) => {
  return axios.post<RecalculateIncentiveResponse>(
    `/project/${projectId}/incentive/recalculate`
  );
};

/** SDCP-1340: create an add-on project (same account) from an existing project. Admin only. */
export const createAddOnProjectReq = async (projectId: ProjectRecord["id"]) => {
  return axios.post<{ project: ProjectRecord }>(`/project/${projectId}/add-on`);
};

export const getGeographicDistributionReq = async (
  search = "",
  signal?: AbortSignal
) => {
  const params = search ? { search } : {};
  return axios.get<GeographicDistributionResponse>(
    `/project/geographic-distribution`,
    { params, signal }
  );
};
